import { add, clamp, scale } from './math'
import { getTrackSample, projectToTrackLocal } from './track'
import type { Track, VehicleState } from './types'

export const ENGINE_FATIGUE_RESPAWN = 40
export const ENGINE_FATIGUE_RECOVERY_PER_SECOND = 1.35
export const OFFTRACK_RESPAWN_SAMPLE_OFFSET = 18

export interface OffTrackResult {
  vehicle: VehicleState
  exploded: boolean
  railBreakSide: -1 | 1 | null
  projectionSampleIndex: number
}

export function getRailBreakSide(previousLateral: number, currentLateral: number, trackWidth: number): -1 | 1 | null {
  if (previousLateral <= trackWidth && currentLateral > trackWidth) return 1
  if (previousLateral >= -trackWidth && currentLateral < -trackWidth) return -1
  return null
}

function respawnBehind(vehicle: VehicleState, track: Track, sampleIndex: number): VehicleState {
  const respawn = getTrackSample(track, sampleIndex - OFFTRACK_RESPAWN_SAMPLE_OFFSET)
  const lookahead = getTrackSample(track, respawn.index + 4)
  const heading = Math.atan2(lookahead.position.x - respawn.position.x, lookahead.position.y - respawn.position.y)
  return {
    ...vehicle,
    position: { ...respawn.position },
    velocity: { x: 0, y: 0 },
    heading,
    speed: 0,
    steer: 0,
    boostActive: false,
    engineFatigue: ENGINE_FATIGUE_RESPAWN,
    offTrack: false,
    respawnCount: vehicle.respawnCount + 1,
    trackSampleIndex: respawn.index,
    lastSafePosition: { ...respawn.position },
    lastSafeHeading: heading,
    drift: { ...vehicle.drift, active: false, angle: 0, charge: 0, duration: 0 },
  }
}

export function stepOffTrackState(
  vehicle: VehicleState,
  track: Track,
  dt: number,
  previousLateral?: number,
): OffTrackResult {
  const projection = projectToTrackLocal(track, vehicle.position, vehicle.trackSampleIndex)
  const lateral = projection.lateral
  const excess = Math.max(0, Math.abs(lateral) - track.width)
  const offTrack = excess > 0
  const railBreakSide = previousLateral === undefined ? null : getRailBreakSide(previousLateral, lateral, track.width)
  const speedRatio = clamp(Math.abs(vehicle.speed) / 56, 0, 1.5)
  const depthRatio = clamp(excess / track.width, 0, 2)
  const fatigueRate = offTrack ? 6 + speedRatio * 4 + depthRatio * 12 : -ENGINE_FATIGUE_RECOVERY_PER_SECOND
  const engineFatigue = clamp(vehicle.engineFatigue + fatigueRate * Math.max(0, dt), 0, 100)
  if (engineFatigue >= 100) {
    return {
      vehicle: respawnBehind({ ...vehicle, engineFatigue }, track, projection.sampleIndex),
      exploded: true,
      railBreakSide,
      projectionSampleIndex: projection.sampleIndex,
    }
  }
  const sample = getTrackSample(track, projection.sampleIndex)
  const safelyOnTrack = Math.abs(lateral) <= track.width * 0.78
  return {
    vehicle: {
      ...vehicle,
      engineFatigue,
      offTrack,
      trackSampleIndex: projection.sampleIndex,
      lastSafePosition: safelyOnTrack ? add(sample.position, scale(sample.normal, lateral)) : vehicle.lastSafePosition,
      lastSafeHeading: safelyOnTrack ? vehicle.heading : vehicle.lastSafeHeading,
    },
    exploded: false,
    railBreakSide,
    projectionSampleIndex: projection.sampleIndex,
  }
}
